import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import { Checkbox } from '../TovCheckbox/TovCheckbox';
import { Combobox, type ComboboxProps } from '../TovCombobox/TovCombobox';
import { TovButton, type TovButtonProps } from '../TovButton/TovButton';
import { TovField } from '../TovField/TovField';
import { Input, type InputProps, Textarea } from '../TovInput/TovInput';
import {
  LocalizedTextarea,
  LocalizedTextInput,
} from '../TovLocalizedFields/TovLocalizedFields';
import { Select, type SelectProps } from '../TovNativeSelect/TovNativeSelect';

export {
  useForm as useTovForm,
  useStore as useTovFormStore,
} from '@tanstack/react-form';

export type TovFormSubmitState = 'idle' | 'submitting' | 'success' | 'error';

export function getTovFormSubmitState(input: {
  isSubmitSuccessful: boolean;
  isSubmitting: boolean;
  submissionAttempts: number;
}): TovFormSubmitState {
  if (input.isSubmitting) {
    return 'submitting';
  }

  if (input.isSubmitSuccessful) {
    return 'success';
  }

  return input.submissionAttempts > 0 ? 'error' : 'idle';
}

export function getTovFormErrorMessage(
  error: unknown,
  fallbackMessage: string,
) {
  return error instanceof Error ? error.message : fallbackMessage;
}

export function getTovFormFieldErrorMessage(errors: unknown[]) {
  const firstError = errors.find((error) => error !== undefined);

  if (!firstError) {
    return undefined;
  }

  if (typeof firstError === 'string') {
    return firstError;
  }

  if (firstError instanceof Error) {
    return firstError.message;
  }

  if (
    typeof firstError === 'object' &&
    firstError !== null &&
    'message' in firstError &&
    typeof firstError.message === 'string'
  ) {
    return firstError.message;
  }

  return String(firstError);
}

type TovFormFieldRenderInput = {
  handleBlur?: () => void;
  handleChange: (value: never) => void;
  state: {
    meta?: {
      errors?: unknown[];
    };
    value: unknown;
  };
};

export type TovFormFieldAdapterForm = {
  Field: unknown;
  handleSubmit?: () => Promise<void> | void;
  store?: unknown;
};

interface TovFormFieldBaseProps {
  fieldClassName?: string;
  form: TovFormFieldAdapterForm;
  help?: ReactNode;
  hint?: ReactNode;
  label?: ReactNode;
  name: string;
}

function resolveTovFormFieldHint(
  hint: ReactNode,
  field: TovFormFieldRenderInput,
) {
  return getTovFormFieldErrorMessage(field.state.meta?.errors ?? []) ?? hint;
}

type TovFormFieldComponent = (props: {
  children: (field: TovFormFieldRenderInput) => ReactNode;
  name: string;
}) => ReactNode;

export type TovFormLocalizedFieldValue = Record<string, string>;

function isTovFormLocalizedFieldValue(
  value: unknown,
): value is TovFormLocalizedFieldValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every((entry) => typeof entry === 'string')
  );
}

function formatTovFormLocalizedFieldValue(
  value: unknown,
): TovFormLocalizedFieldValue {
  return isTovFormLocalizedFieldValue(value) ? value : {};
}

export function resolveTovLocalizedFormFieldValue(input: {
  currentValue: unknown;
  localizedValue: TovFormLocalizedFieldValue;
  primaryLocaleCode: string;
}) {
  const resolvedValue = input.localizedValue[input.primaryLocaleCode];

  if (resolvedValue !== undefined) {
    return resolvedValue;
  }

  return typeof input.currentValue === 'string' ? input.currentValue : '';
}

export function resolveTovLocalizedFormFieldStorageValue(input: {
  currentValue: unknown;
  localizedValue: TovFormLocalizedFieldValue;
  primaryLocaleCode: string;
  valueMode?: 'localized' | 'resolved';
}) {
  if (input.valueMode === 'localized') {
    return input.localizedValue;
  }

  return resolveTovLocalizedFormFieldValue(input);
}

export function formatTovFormStringFieldValue(value: unknown) {
  return value === undefined || value === null ? '' : String(value);
}

export interface TovFormTextFieldProps
  extends
    TovFormFieldBaseProps,
    Omit<
      InputProps,
      | 'children'
      | 'defaultValue'
      | 'form'
      | 'name'
      | 'onBlur'
      | 'onChange'
      | 'value'
    > {
  formatValue?: (value: unknown) => string | number | readonly string[];
  onValueChange?: (value: string) => void;
  parseValue?: (value: string) => unknown;
}

export function TovFormTextField({
  form,
  fieldClassName,
  formatValue = formatTovFormStringFieldValue,
  help,
  hint,
  label,
  name,
  onValueChange,
  parseValue = (value) => value,
  ...props
}: TovFormTextFieldProps) {
  const Field = form.Field as TovFormFieldComponent;

  return (
    <Field name={name}>
      {(field) => (
        <TovField
          className={fieldClassName}
          help={help}
          hint={resolveTovFormFieldHint(hint, field)}
          label={label}
        >
          <Input
            {...props}
            value={formatValue(field.state.value)}
            onBlur={field.handleBlur}
            onChange={(event) => {
              field.handleChange(parseValue(event.target.value) as never);
              onValueChange?.(event.target.value);
            }}
          />
        </TovField>
      )}
    </Field>
  );
}

export interface TovFormTextareaFieldProps
  extends
    TovFormFieldBaseProps,
    Omit<
      ComponentPropsWithoutRef<'textarea'>,
      | 'children'
      | 'defaultValue'
      | 'form'
      | 'name'
      | 'onBlur'
      | 'onChange'
      | 'value'
    > {
  formatValue?: (value: unknown) => string;
  onValueChange?: (value: string) => void;
}

export function TovFormTextareaField({
  form,
  fieldClassName,
  formatValue = formatTovFormStringFieldValue,
  help,
  hint,
  label,
  name,
  onValueChange,
  ...props
}: TovFormTextareaFieldProps) {
  const Field = form.Field as TovFormFieldComponent;

  return (
    <Field name={name}>
      {(field) => (
        <TovField
          className={fieldClassName}
          help={help}
          hint={resolveTovFormFieldHint(hint, field)}
          label={label}
        >
          <Textarea
            {...props}
            value={formatValue(field.state.value)}
            onBlur={field.handleBlur}
            onChange={(event) => {
              field.handleChange(event.target.value as never);
              onValueChange?.(event.target.value);
            }}
          />
        </TovField>
      )}
    </Field>
  );
}

type TovFormLocalizedFieldBaseProps = TovFormFieldBaseProps & {
  localizedValue?: TovFormLocalizedFieldValue;
  onLocalizedValueChange?: (value: TovFormLocalizedFieldValue) => void;
  onValueChange?: (value: string) => void;
  primaryLocaleCode: string;
  valueMode?: 'localized' | 'resolved';
};

type TovFormLocalizedTextFieldControlProps = Omit<
  ComponentPropsWithoutRef<typeof LocalizedTextInput>,
  'form' | 'name' | 'onBlur' | 'onChange' | 'value'
>;

export interface TovFormLocalizedTextFieldProps
  extends
    TovFormLocalizedFieldBaseProps,
    TovFormLocalizedTextFieldControlProps {}

export function TovFormLocalizedTextField({
  form,
  fieldClassName,
  help,
  hint,
  label,
  localizedValue,
  name,
  onLocalizedValueChange,
  onValueChange,
  primaryLocaleCode,
  valueMode = 'resolved',
  ...props
}: TovFormLocalizedTextFieldProps) {
  const Field = form.Field as TovFormFieldComponent;

  return (
    <Field name={name}>
      {(field) => (
        <TovField
          className={fieldClassName}
          help={help}
          hint={resolveTovFormFieldHint(hint, field)}
          label={label}
        >
          <LocalizedTextInput
            {...props}
            value={
              localizedValue ??
              formatTovFormLocalizedFieldValue(field.state.value)
            }
            onBlur={field.handleBlur}
            onChange={(nextValue) => {
              const nextFormValue = resolveTovLocalizedFormFieldValue({
                currentValue: field.state.value,
                localizedValue: nextValue,
                primaryLocaleCode,
              });

              field.handleChange(
                resolveTovLocalizedFormFieldStorageValue({
                  currentValue: field.state.value,
                  localizedValue: nextValue,
                  primaryLocaleCode,
                  valueMode,
                }) as never,
              );
              onLocalizedValueChange?.(nextValue);
              onValueChange?.(nextFormValue);
            }}
          />
        </TovField>
      )}
    </Field>
  );
}

type TovFormLocalizedTextareaFieldControlProps = Omit<
  ComponentPropsWithoutRef<typeof LocalizedTextarea>,
  'form' | 'name' | 'onBlur' | 'onChange' | 'value'
>;

export interface TovFormLocalizedTextareaFieldProps
  extends
    TovFormLocalizedFieldBaseProps,
    TovFormLocalizedTextareaFieldControlProps {}

export function TovFormLocalizedTextareaField({
  form,
  fieldClassName,
  help,
  hint,
  label,
  localizedValue,
  name,
  onLocalizedValueChange,
  onValueChange,
  primaryLocaleCode,
  valueMode = 'resolved',
  ...props
}: TovFormLocalizedTextareaFieldProps) {
  const Field = form.Field as TovFormFieldComponent;

  return (
    <Field name={name}>
      {(field) => (
        <TovField
          className={fieldClassName}
          help={help}
          hint={resolveTovFormFieldHint(hint, field)}
          label={label}
        >
          <LocalizedTextarea
            {...props}
            value={
              localizedValue ??
              formatTovFormLocalizedFieldValue(field.state.value)
            }
            onBlur={field.handleBlur}
            onChange={(nextValue) => {
              const nextFormValue = resolveTovLocalizedFormFieldValue({
                currentValue: field.state.value,
                localizedValue: nextValue,
                primaryLocaleCode,
              });

              field.handleChange(
                resolveTovLocalizedFormFieldStorageValue({
                  currentValue: field.state.value,
                  localizedValue: nextValue,
                  primaryLocaleCode,
                  valueMode,
                }) as never,
              );
              onLocalizedValueChange?.(nextValue);
              onValueChange?.(nextFormValue);
            }}
          />
        </TovField>
      )}
    </Field>
  );
}

export interface TovFormSelectFieldProps
  extends
    TovFormFieldBaseProps,
    Omit<
      SelectProps,
      'defaultValue' | 'form' | 'name' | 'onBlur' | 'onChange' | 'value'
    > {
  formatValue?: (value: unknown) => string;
  onValueChange?: (value: string) => void;
}

export function TovFormSelectField({
  children,
  form,
  fieldClassName,
  formatValue = formatTovFormStringFieldValue,
  help,
  hint,
  label,
  name,
  onValueChange,
  ...props
}: TovFormSelectFieldProps) {
  const Field = form.Field as TovFormFieldComponent;

  return (
    <Field name={name}>
      {(field) => (
        <TovField
          className={fieldClassName}
          help={help}
          hint={resolveTovFormFieldHint(hint, field)}
          label={label}
        >
          <Select
            {...props}
            value={formatValue(field.state.value)}
            onBlur={field.handleBlur}
            onChange={(event) => {
              field.handleChange(event.target.value as never);
              onValueChange?.(event.target.value);
            }}
          >
            {children}
          </Select>
        </TovField>
      )}
    </Field>
  );
}

export interface TovFormComboboxFieldProps
  extends
    TovFormFieldBaseProps,
    Omit<
      ComboboxProps,
      | 'defaultValue'
      | 'form'
      | 'label'
      | 'name'
      | 'onBlur'
      | 'onValueChange'
      | 'value'
    > {
  formatValue?: (value: unknown) => string;
  onValueChange?: (value: string) => void;
}

export function TovFormComboboxField({
  form,
  fieldClassName,
  formatValue = formatTovFormStringFieldValue,
  help,
  hint,
  label,
  name,
  onValueChange,
  ...props
}: TovFormComboboxFieldProps) {
  const Field = form.Field as TovFormFieldComponent;
  const comboboxLabel = typeof label === 'string' ? label : undefined;

  return (
    <Field name={name}>
      {(field) => (
        <TovField
          className={fieldClassName}
          help={help}
          hint={resolveTovFormFieldHint(hint, field)}
          label={label}
        >
          <Combobox
            {...props}
            label={comboboxLabel}
            value={formatValue(field.state.value)}
            onValueChange={(value) => {
              field.handleChange(value as never);
              onValueChange?.(value);
            }}
          />
        </TovField>
      )}
    </Field>
  );
}

export interface TovFormCheckboxFieldProps
  extends
    TovFormFieldBaseProps,
    Omit<
      ComponentPropsWithoutRef<typeof Checkbox>,
      | 'checked'
      | 'defaultChecked'
      | 'form'
      | 'label'
      | 'name'
      | 'onBlur'
      | 'onCheckedChange'
    > {
  formatValue?: (value: unknown) => boolean;
  onValueChange?: (value: boolean) => void;
}

export function TovFormCheckboxField({
  form,
  fieldClassName,
  formatValue = (value) => value === true,
  help,
  hint,
  label,
  name,
  onValueChange,
  ...props
}: TovFormCheckboxFieldProps) {
  const Field = form.Field as TovFormFieldComponent;

  return (
    <Field name={name}>
      {(field) => (
        <TovField
          className={fieldClassName}
          help={help}
          hint={resolveTovFormFieldHint(hint, field)}
          label={label}
        >
          <Checkbox
            {...props}
            checked={formatValue(field.state.value)}
            onBlur={field.handleBlur}
            onCheckedChange={(checked) => {
              const nextValue = checked === true;
              field.handleChange(nextValue as never);
              onValueChange?.(nextValue);
            }}
          />
        </TovField>
      )}
    </Field>
  );
}

export type TovFormSubmitButtonProps = Omit<TovButtonProps, 'type'>;

export function TovFormSubmitButton(props: TovFormSubmitButtonProps) {
  return <TovButton {...props} type="submit" />;
}
